import React, { ReactNode } from "react";
import { trackError } from "../utils/error-tracking";

interface ErrorBoundaryProps {
	returnNull?: boolean;
	children: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: string;
	errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);

		// Define a state variable to track whether is an error or not
		this.state = {
			hasError: false,
			error: "",
			errorInfo: null,
		};
	}

	/**
	 * Whether the data-fetch reset below has already been spent.
	 *
	 * Kept off state deliberately: it must not trigger a render, and it must
	 * survive the reset that clears everything else.
	 */
	private retriedDataFetch = false;

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		this.setState({ errorInfo });

		// Track error to analytics
		trackError(error);

		// Vercel edge cache failures surface as "Failed to load static props" —
		// only the /_next/data JSON fetch 500s. Reset the boundary and let the
		// page component's own API fallback load the data instead.
		//
		// Once only. Resetting on every catch means a child that keeps throwing
		// loops here — catch, reset, re-render, throw — until React gives up
		// with "Maximum update depth exceeded", which is a worse crash than the
		// screen this was avoiding, and locks the tab on the way there.
		if (
			!this.retriedDataFetch &&
			(error.message?.includes('Failed to load static props') ||
				error.message?.includes('Failed to load script'))
		) {
			this.retriedDataFetch = true;
			this.setState({ hasError: false, error: '', errorInfo: null });
			return;
		}
	}

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error: error.message };
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
					<h2 className="text-xl mb-4">Something went wrong</h2>
					<p className="text-gray-500 mb-6">This page had a problem loading.</p>
					<div className="flex gap-4">
						<button
							type="button"
							className="px-6 py-2 cursor-pointer border rounded"
							onClick={() => window.location.reload()}
						>
							Reload page
						</button>
						<button
							type="button"
							className="px-6 py-2 cursor-pointer border rounded"
							onClick={() => { window.location.href = '/'; }}
						>
							Go home
						</button>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
